import { Link } from "react-router-dom";
import {
  meetingCalendarStatus,
  nextClinicianMeeting,
  type ClinicianMeeting,
} from "@care-by-ella/contracts";
import { PatientLabel } from "@/components/PatientLabel";
import { BackupPhoneLine } from "@/components/meeting/BackupPhoneLine";
import { useViewPath } from "@/components/SupportView";
import { patientMeetingHref } from "@/lib/patient-links";
import {
  countdownText,
  meetingKindLabel,
  whenText,
} from "@/lib/meeting-calendar";
import { useNow } from "@/lib/use-now";
import { MeetingSignals } from "./MeetingSignals";
import { MeetingStatusTag } from "./MeetingStatusTag";

// TES-1233 — Emelie: "Adminstarten visar alltid Nästa möte med patient,
// mötestyp, klockslag, nedräkning och Öppna mötet."
//
// Always: with no meeting ahead the card stays and says so, rather than
// leaving the top of the page empty — an absent card and a missing meeting
// look the same. A meeting that has started is still "next" until it is
// over, so the warning that the doctor has not opened it stays on the card.
export function NextMeetingCard({
  meetings,
  loading,
}: {
  meetings: ClinicianMeeting[] | null;
  loading: boolean;
}) {
  const now = useNow();
  const viewPath = useViewPath();
  const next = nextClinicianMeeting(meetings, now);
  const ongoing = next ? meetingCalendarStatus(next, now) === "ongoing" : false;

  return (
    <section
      aria-labelledby="next-meeting-heading"
      className={`flex flex-col gap-sm rounded-md border bg-surface p-md ${
        ongoing ? "border-brand border-2" : "border-surfaceMuted"
      }`}
    >
      <h2
        id="next-meeting-heading"
        className="font-display text-lg tracking-tight"
      >
        Nästa möte
      </h2>
      {!next ? (
        <p className="text-sm text-textSecondary">
          {loading && !meetings ? "Hämtar…" : "Inga schemalagda möten just nu."}
        </p>
      ) : (
        <div className="flex flex-col gap-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-col gap-xs">
            <div className="flex flex-wrap items-baseline gap-x-md gap-y-xs">
              <span className="text-xl font-medium">
                {whenText(next.scheduledAt, new Date(now))}
              </span>
              <MeetingStatusTag status={meetingCalendarStatus(next, now)} />
            </div>
            <p className="min-w-0 text-sm">
              <PatientLabel
                patientId={next.patient.id}
                name={next.patient.name}
                personalNumberLast6={next.patient.personalNumberLast6}
                link
              />
              <span className="text-textSecondary">
                {" "}
                · {meetingKindLabel(next)}
              </span>
            </p>
            <p className="text-sm text-textSecondary">
              {countdownText(next, now)}
            </p>
            <MeetingSignals meeting={next} now={now} />
            {/* TES-1306 — the meeting card's backup number. */}
            <BackupPhoneLine backupPhone={next.patient.backupPhone} />
          </div>
          <Link
            to={viewPath(patientMeetingHref(next.patient.id, next.id))}
            className="shrink-0 self-start rounded-md bg-brand px-md py-sm text-sm font-medium text-textOnBrand hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 sm:self-center"
          >
            Öppna mötet
          </Link>
        </div>
      )}
    </section>
  );
}
